import { Modal, Form, InputNumber, Select, DatePicker, Input, Button, Row, Col, Typography, Alert, Spinner, message, QRCode } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { inr2 } from '../../lib/format';
import { useHasRole, usePermission } from '../../store/auth';
import { bankAcctOption } from '../billing/PaymentFormModal';

interface Props {
  open: boolean;
  onClose: () => void;
  orderId: string;
  orderNo?: string;
}

/*
 * This dialog is used by two very different people, and the payment MODES are not the same for both.
 *
 * A field rep standing in the shop can be handed notes — that is a real event, and it makes them
 * accountable for the cash until they hand it over. A retailer paying from their own login cannot
 * hand cash to a web page: there is nobody on the other side to receive it and nobody to hold it
 * afterwards. Offering them "cash" invited a self-declared payment that reduces their own dues with
 * no money in any account and no person accountable for it — so cash is offered to FIELD STAFF and
 * back-office roles only.
 *
 * Plain language throughout: the stored values are SCREAMING_SNAKE constants and the list used to
 * render them raw, so the picker read "NEFT" / "CHEQUE" as database keys.
 */
const ONLINE_MODES = ['UPI', 'NEFT', 'RTGS', 'IMPS', 'CHEQUE', 'CARD', 'OTHER'];
const CASH_MODE = 'CASH';
const MODE_LABEL: Record<string, string> = {
  UPI: 'UPI', NEFT: 'NEFT transfer', RTGS: 'RTGS transfer', IMPS: 'IMPS transfer',
  CHEQUE: 'Cheque', CARD: 'Card', OTHER: 'Other',
};

/**
 * Retailer (or SP/SH on behalf) pays for an ORDER. Submits a PENDING payment linked to the order —
 * an accountant approves it before it posts to the ledger. No invoice allocation.
 */
export function PayOrderModal({ open, onClose, orderId, orderNo }: Props) {
  /* Cash only for the people who can physically be handed it. A retailer paying from their own
     login has nobody to hand it to; the label says who receives it, so it cannot be misread. */
  const canTakeCash = useHasRole('SALES_PERSON', 'SALES_HEAD', 'ADMIN', 'SUPER_ADMIN', 'ACCOUNTS');
  /*
   * SOMEBODY WHO APPROVES PAYMENTS IS NOT SUBMITTING ONE FOR APPROVAL.
   *
   * Their payment used to join the queue and wait for them to approve it themselves — a step that
   * decides nothing, while the dealer's outstanding stays wrong until it is performed. The server
   * now posts their submission straight away, which means this dialog has to collect the one thing
   * approval asks for and this form never did: WHICH company account the money landed in.
   */
  const canPostDirectly = usePermission('PAYMENT.APPROVE');
  const modeOptions = [
    ...ONLINE_MODES.map((m) => ({ value: m, label: MODE_LABEL[m] ?? m })),
    ...(canTakeCash ? [{ value: CASH_MODE, label: 'Cash handed to me' }] : []),
  ];

  const [form] = Form.useForm();
  const qc = useQueryClient();
  /*
   * PROOF IS REQUIRED WHERE PROOF EXISTS.
   *
   * A transfer leaves a screenshot and a bank leaves a statement line, so a non-cash payment must
   * carry one — it is the only independent evidence the retailer actually paid. Cash counted into
   * someone's hand leaves nothing to photograph, and insisting on a file there buys a picture of a
   * desk, not assurance.
   *
   * What makes cash safe is different and stronger: the collection is recorded against a NAMED
   * custodian who stays accountable for those notes until they are handed over or banked — and the
   * hand-over is where a slip is mandatory, because that is the claim a bank can contradict.
   */
  const payMode = Form.useWatch('mode', form);
  const proofRequired = payMode !== CASH_MODE;
  const amount = Form.useWatch('amount', form);

  const { data: summary, isLoading } = useQuery({
    queryKey: ['order-pay-summary', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/payment-summary` }),
    enabled: open && !!orderId,
  });
  const { data: pay } = useQuery({
    queryKey: ['company-pay-info'],
    queryFn: () => request<any>({ url: '/company/bank-accounts/pay-info' }),
    enabled: open,
  });
  // The company accounts that can RECEIVE the money — only an approver posts directly, so only they
  // are asked for it (a retailer or rep never picks the seller's account; the desk does, at review).
  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
    enabled: open && canPostDirectly,
  });
  const bankOpts: any[] = Array.isArray(bankAccounts) ? bankAccounts : ((bankAccounts as any)?.data ?? []);
  // How much account credit (advance-in-credit) can be applied to this order right now. Returns 0 when the
  // feature is off, so the whole credit UI simply doesn't show.
  const { data: creditPreview, isLoading: creditLoading } = useQuery({
    queryKey: ['order-credit-preview', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/credit-preview` }),
    enabled: open && !!orderId,
  });
  // Both queries gate the body: the amount we seed is `due − credit − already-submitted`, so mounting
  // the form before the credit preview lands would seed it from a number that is about to change.
  const loading = isLoading || creditLoading;

  const due = Number(summary?.due ?? 0);
  const applicable = Math.round(Number(creditPreview?.applicable ?? 0) * 100) / 100;
  const hasCredit = applicable > 0.009;
  /*
   * WHAT CAN ACTUALLY BE SUBMITTED — not just what the order still owes.
   *
   * The server refuses an amount that, together with submissions already awaiting approval, would
   * overshoot the order; the dialog ignored those and invited the full due, so on an order with ₹100
   * pending it offered ₹144 and the submit came back 400. The headline stays the true due — that is
   * what the dealer owes — but the amount field is capped at what this submission may add.
   */
  const pendingAmt = Math.round(Number(summary?.pendingAmt ?? 0) * 100) / 100;
  const netToPay = Math.max(0, Math.round((due - applicable) * 100) / 100);
  const submittable = Math.max(0, Math.round((netToPay - pendingAmt) * 100) / 100);
  const fullyCovered = hasCredit && netToPay <= 0.009;

  /*
   * SEEDED THROUGH `initialValues`, NOT `setFieldsValue` IN AN EFFECT.
   *
   * The effect that used to do this never landed a single value — the dialog opened with Amount,
   * Date and Mode all blank. `ui/Form` seeds its store from `initialValues` ONCE per mount, and it
   * does so DURING RENDER via an `inited` ref (see ui/Form.tsx). This form is not mounted while the
   * summary is loading, so it mounts for the first time in the very commit the data arrives — the
   * same commit the effect fired in — and the render-phase wipe raced the effect's write.
   *
   * Seeding at mount removes the race entirely: by the time the form exists, both queries have
   * settled and the numbers are final. `key` re-mounts it if the reader switches order without the
   * dialog closing, so a stale seed can never be left behind. See the form-seed modal pattern.
   *
   * The amount is `submittable`, NOT `netToPay`: on an order that already has a submission awaiting
   * approval, the true due is more than this submission may add, and the field's own `max` is
   * `submittable` — seeding the larger figure would open the dialog on a value that fails its own
   * validation.
   */
  const seed = {
    amount: submittable > 0.009 ? submittable : undefined,
    mode: 'UPI',
    paymentDate: dayjs(),
  };

  const invalidateAll = () => {
    for (const key of [['orders'], ['order', orderId], ['order-pay-summary', orderId], ['order-credit-preview', orderId], ['order-payments', orderId], ['payments'], ['badges'], ['retailer']]) {
      qc.invalidateQueries({ queryKey: key as any });
    }
  };
  // Apply the retailer's account credit to this order (ledger-neutral) — used both for zero-cash checkout and
  // just before a cash payment covers the remaining net.
  const applyCredit = useConfirmedMutation({ title: "Apply the retailer’s credit to this order?", content: "Credit in hand is set against what is payable on this order.", okText: "Yes, apply" }, {
    mutationFn: () => request<any>({ url: `/orders/${orderId}/apply-credit`, method: 'POST' }),
  });
  const settleWithCredit = async () => {
    try {
      const r = await applyCredit.mutateAsync();
      message.success(`₹${Number(r.applied ?? applicable).toLocaleString('en-IN')} credit applied — order settled`);
      invalidateAll();
      onClose();
    } catch (e: any) {
      message.error(e.response?.data?.error?.message ?? 'Failed to apply credit');
    }
  };

  /* WHAT THE CLICK DOES DEPENDS ON WHO CLICKS IT. A verifier's payment posts at once; everybody else's waits for
     Accounts. Telling a rep it "posts to the retailer's ledger" described a step that had not happened (E2E 15 Sep). */
  const submitConfirm = canPostDirectly
    ? { title: 'Record this payment against the order?', content: 'It posts to the retailer’s ledger straight away and reduces what they owe.', okText: 'Yes, record' }
    : payMode === CASH_MODE
      ? { title: 'Submit this cash for verification?', content: 'Accounts verifies it before the dealer’s outstanding comes down. Until you bank it, the cash stays against your name.', okText: 'Yes, submit' }
      : { title: 'Submit this payment for verification?', content: 'Accounts checks it against the bank before the dealer’s outstanding comes down.', okText: 'Yes, submit' };
  const submit = useConfirmedMutation(submitConfirm, {
    mutationFn: (v: any) => request<any>({
      url: `/orders/${orderId}/pay`, method: 'POST',
      data: {
        paymentDate: v.paymentDate.format('YYYY-MM-DD'),
        amount: v.amount, mode: v.mode, referenceNo: v.referenceNo, utr: v.utr,
        attachmentUrls: v.attachmentUrls,
        receivedBankAccountId: payMode === CASH_MODE ? undefined : (v.receivedBankAccountId || undefined),
      },
    }),
    onSuccess: (r) => {
      message.success(r?.approvalStatus === 'APPROVED'
        ? `Payment ${r.paymentNo} recorded — the dealer's outstanding is updated`
        : `Payment ${r.paymentNo} submitted — pending approval`);
      invalidateAll();
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to submit payment'),
  });
  // Cash-with-credit: apply the available credit first (ledger-neutral), then submit the cash for the net.
  const handleFinish = async (v: any) => {
    if (hasCredit) {
      try { await applyCredit.mutateAsync(); } catch (e: any) { message.error(e.response?.data?.error?.message ?? 'Failed to apply credit'); return; }
    }
    submit.mutate(v);
  };

  const upiString = pay?.upiString as string | undefined;
  const qrValue = upiString ? `${upiString}${amount ? `&am=${Number(amount).toFixed(2)}` : ''}` : '';

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={`Pay — Order ${orderNo ?? ''}`} width={680}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}><Spinner /></div>
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col span={hasCredit ? 6 : 8}><Typography.Text type="secondary">{summary?.netOfAdvanceDiscount ? 'Total after advance discount' : 'Order total'}</Typography.Text><div style={{ fontSize: 16, fontWeight: 600 }}>{inr2(summary?.grandTotal)}</div></Col>
            <Col span={hasCredit ? 6 : 8}><Typography.Text type="secondary">Paid</Typography.Text><div style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-brand-600)' }}>{inr2(summary?.paid)}</div></Col>
            {hasCredit && <Col span={6}><Typography.Text type="secondary">Credit applied</Typography.Text><div style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-brand-600)' }}>−{inr2(applicable)}</div></Col>}
            <Col span={hasCredit ? 6 : 8}><Typography.Text type="secondary">{hasCredit ? 'Net to pay' : 'Due'}</Typography.Text><div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-danger-500)' }}>{inr2(netToPay)}</div></Col>
          </Row>

          {Number(summary?.pendingAmt) > 0 && (
            <Alert type="info" style={{ marginBottom: 12 }}
              title={`${inr2(pendingAmt)} already submitted and awaiting approval.`}
              description={submittable > 0.009
                ? `Until that is approved or rejected, you can submit up to ${inr2(submittable)} more against this order.`
                : 'That already covers the balance — nothing more can be submitted until it is approved or rejected.'} />
          )}

          {hasCredit && (
            <Alert type="success" style={{ marginBottom: 12 }} title={
              fullyCovered
                ? `You have ${inr2(applicable)} account credit — it covers this order in full, no cash needed.`
                : `${inr2(applicable)} of your account credit will be applied to this order — you pay only ${inr2(netToPay)}.`
            } />
          )}

          {due <= 0.01 ? (
            <Alert type="success" title="This order is fully paid." />
          ) : fullyCovered ? (
            <Button variant="primary" block loading={applyCredit.isPending} onClick={settleWithCredit}>
              Settle order with {inr2(applicable)} account credit
            </Button>
          ) : (
            <Form key={`pay-${orderId}`} form={form} layout="vertical" initialValues={seed} onFinish={handleFinish}>
              <Row gutter={16}>
                <Col xs={24} md={14}>
                  <Row gutter={12}>
                    {/* Two to a row from 576px; one per row on a phone, where the date field cannot shrink below its own value. */}
                    <Col xs={24} sm={12}><Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={1} max={submittable} step={100} /></Form.Item></Col>
                    <Col xs={24} sm={12}><Form.Item name="paymentDate" label="Date" rules={[{ required: true }]}><DatePicker className="w-full" /></Form.Item></Col>
                    <Col xs={24} sm={12}><Form.Item name="mode" label="Mode" rules={[{ required: true }]}><Select options={modeOptions} /></Form.Item></Col>
                    <Col xs={24} sm={12}><Form.Item name="referenceNo" label="Reference No"><Input placeholder="UPI / cheque ref" maxLength={40} /></Form.Item></Col>
                    <Col span={24}><Form.Item name="utr" label="UTR / Bank Ref"><Input maxLength={40} /></Form.Item></Col>
                    {/* Only a poster names the receiving account, and only for money that went through
                        a bank — cash handed to a rep lands in no account at all. */}
                    {canPostDirectly && payMode !== CASH_MODE && (
                      <Col span={24}>
                        <Form.Item
                          name="receivedBankAccountId"
                          label="Received in (bank account)"
                          rules={[{ required: true, message: 'Select the receiving bank account' }]}
                          tooltip="Which of your company bank accounts this payment landed in."
                        >
                          <Select
                            allowClear showSearch optionFilterProp="searchLabel"
                            placeholder={bankOpts.length ? 'Select the receiving bank account' : 'No bank accounts configured — add under Masters → Company bank details'}
                            disabled={!bankOpts.length}
                            options={bankOpts.map(bankAcctOption)}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    <Col span={24}>
                      <Form.Item
                        name="attachmentUrls"
                        label={proofRequired ? 'Payment proof' : 'Payment proof (optional)'}
                        rules={proofRequired ? [{ required: true, type: 'array', min: 1, message: 'Upload your payment screenshot / receipt' }] : []}
                        extra={proofRequired
                          ? 'Required — the UPI/transfer screenshot or the receipt.'
                          : 'Not needed for cash. You stay accountable for these notes until you hand them over — the slip is attached then.'}
                      >
                        <MultiFileUploadField category="receipt" buttonLabel="Upload proof" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} md={10}>
                  <div style={{ textAlign: 'center', border: '1px dashed var(--gb-border)', borderRadius: 8, padding: 12 }}>
                    <Typography.Text strong>Scan to Pay (UPI)</Typography.Text>
                    <div style={{ margin: '10px 0', display: 'flex', justifyContent: 'center' }}>
                      {qrValue ? (
                        <QRCode value={qrValue} size={150} />
                      ) : pay?.upiQrUrl ? (
                        <img src={pay.upiQrUrl} width={150} height={150} style={{ objectFit: 'contain' }} alt="UPI QR" />
                      ) : (
                        <Typography.Text type="secondary">UPI not configured</Typography.Text>
                      )}
                    </div>
                    {pay?.upiId && <div style={{ fontSize: 12 }}><b>UPI:</b> {pay.upiId}</div>}
                    {pay?.bankName && <div style={{ fontSize: 11, color: '#888' }}>{pay.bankName} · A/c {pay.accountNumber} · {pay.ifsc}</div>}
                  </div>
                </Col>
              </Row>
              <Button variant="primary" type="submit" loading={submit.isPending || applyCredit.isPending} block style={{ marginTop: 4 }}>
                {hasCredit
                  ? `Apply ${inr2(applicable)} credit & ${canPostDirectly ? 'record' : 'submit'} ${inr2(netToPay)}`
                  : canPostDirectly ? 'Record payment' : 'Submit payment for approval'}
              </Button>
              <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0, textAlign: 'center' }}>
                {canPostDirectly
                  ? 'You verify payments, so this one posts straight to the dealer’s ledger — no approval step.'
                  : proofRequired
                    ? 'Your payment is recorded only after an accountant verifies the proof.'
                    : 'Recorded once an accountant approves it. The cash then sits in your custody until you hand it over.'}
              </Typography.Paragraph>
            </Form>
          )}
        </>
      )}
    </Modal>
  );
}
